import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { daysBetween, isOverdueInStage, daysOverdue } from "@/lib/pipeline";
import { ownerColor, stageColor } from "@/lib/palette";
import { Ban, CheckCircle2, ChevronLeft, ChevronRight, Forward, X, Archive, Merge, History, Moon, Pin, Eye, ThumbsUp, Phone, Voicemail, Mail, MessageSquare, Users } from "lucide-react";
import MergeCardDialog from "@/components/board/MergeCardDialog";
import DocumentsSection from "@/components/board/DocumentsSection";
import ResumeReparse from "@/components/board/ResumeReparse";
import ResumeBox from "@/components/board/ResumeBox";
import { FitChip } from "@/components/board/ResumeNotes";
import { OverdueSiren } from "@/components/OverdueSiren";
import AvidaMark from "@/components/AvidaMark";
import SendEmailDialog from "@/components/SendEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/roles";
import { ROLE_TYPES } from "@/lib/workspace";
import { useNavigate } from "react-router-dom";

const ONBOARDING_CLIENT_ITEMS = [
  ["agreements_signed", "Client agreements & contracts signed"],
  ["home_folder_delivered", "Home folder delivered"],
  ["plan_of_care_reviewed", "Plan of care reviewed with PCW"],
];

const TOUCH_TYPES = [
  { key: "call", label: "Call", Icon: Phone },
  { key: "voicemail", label: "VM", Icon: Voicemail },
  { key: "email", label: "Email", Icon: Mail },
  { key: "text", label: "Text", Icon: MessageSquare },
  { key: "meeting", label: "Meeting", Icon: Users },
];
const TOUCH_OUTCOMES = [
  { key: "reached", label: "Reached" },
  { key: "no_answer", label: "No answer" },
  { key: "left_message", label: "Left message" },
  { key: "callback", label: "Callback set" },
];
const TOUCH_ICON = Object.fromEntries(TOUCH_TYPES.map((t) => [t.key, t.Icon]));
const TOUCH_LABEL = Object.fromEntries(TOUCH_TYPES.map((t) => [t.key, t.key === "voicemail" ? "Voicemail" : t.label]));

export default function CardDetailPanel({
  card: initialCard,
  template,
  templates,
  users,
  lists,
  onClose,
  onChanged,
}) {
  const [card, setCard] = useState(initialCard);
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [showArchive, setShowArchive] = useState(false);
  const archiveReasons = (initialCard.pipeline === "recruiting" ? lists.recruiting_lost_reasons : lists.lost_reasons) || [];
  const [archiveReason, setArchiveReason] = useState(archiveReasons[0] || "");
  const [archiveNote, setArchiveNote] = useState("");
  const [nextDate, setNextDate] = useState(card.next_action_date || "");
  const [nextText, setNextText] = useState(card.next_action_text || "");
  const [fullName, setFullName] = useState(card.full_name || "");
  const [mco, setMco] = useState(card.payer_mco || "");
  const [phone, setPhone] = useState(card.phone || "");
  const [dob, setDob] = useState(card.dob || "");
  const [partners, setPartners] = useState([]);
  const [pcws, setPcws] = useState([]);
  const [agents, setAgents] = useState([]);
  const [efforts, setEfforts] = useState([]);
  const isRecruit = card.pipeline === "recruiting";
  const [position, setPosition] = useState(card.position || "");
  const [email, setEmail] = useState(card.email || "");
  const [certs, setCerts] = useState(card.certifications || "");
  const [clientOptions, setClientOptions] = useState([]);

  const [addingTask, setAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [showMerge, setShowMerge] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [docReload, setDocReload] = useState(0);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [touchType, setTouchType] = useState("");
  const [touchOutcome, setTouchOutcome] = useState("");
  const [touchNote, setTouchNote] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const isSnoozed = !!(card.snoozed_until && card.snoozed_until > new Date().toISOString().slice(0, 10));
  const [snoozeReason, setSnoozeReason] = useState("");
  const snooze = async (days) => {
    const reason = snoozeReason.trim();
    if (!reason) { toast.error("Please add a snooze reason"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/snooze`, { days, reason });
      setCard(data); onChanged?.(data);
      setSnoozeReason("");
      toast.success(`Snoozed ${days} days — paused from your daily lists`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const wake = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/unsnooze`);
      setCard(data); onChanged?.(data);
      toast.success("Woken — back in your active lists");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setCard(initialCard);
    setNextDate(initialCard.next_action_date || "");
    setNextText(initialCard.next_action_text || "");
    setFullName(initialCard.full_name || "");
    setMco(initialCard.payer_mco || "");
    setPhone(initialCard.phone || "");
    setDob(initialCard.dob || "");
    setPosition(initialCard.position || "");
    setEmail(initialCard.email || "");
    setCerts(initialCard.certifications || "");
  }, [initialCard]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    api.get("/referral-partners").then(({ data }) => setPartners(data || [])).catch(() => {});
    api.get("/pcws").then(({ data }) => setPcws(data || [])).catch(() => {});
    api.get("/field-agents").then(({ data }) => setAgents(data || [])).catch(() => {});
    api.get("/marketing-efforts").then(({ data }) => setEfforts(data || [])).catch(() => {});
    if (card.pipeline === "recruiting") {
      api.get("/cards", { params: { status: "active", pipeline: "intake" } })
        .then(({ data }) => setClientOptions((data || []).map((c) => ({ id: c.id, name: c.full_name }))))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    api.get(`/cards/${card.id}/activity`).then(({ data }) => {
      if (active) setActivity(data);
    });
    return () => {
      active = false;
    };
  }, [card.id, card.updated_at]);

  useEffect(() => {
    let active = true;
    api.get(`/cards/${card.id}/notes`).then(({ data }) => { if (active) setNotes(data || []); }).catch(() => {});
    return () => { active = false; };
  }, [card.id]);

  const sortNotes = (arr) => [...arr].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  const addNote = async () => {
    const body = newNote.trim();
    if (!body) return toast.error("Write a note first");
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/notes`, { body });
      setNotes((prev) => [...prev, data.note]);
      setNewNote("");
      if (data.card) { setCard(data.card); onChanged?.(data.card); }
      toast.success("Note added");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const pinNote = async (n) => {
    try {
      const { data } = await api.post(`/cards/${card.id}/notes/${n.id}/pin`, { pinned: !n.pinned });
      setNotes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reactNote = async (n, type) => {
    try {
      const { data } = await api.post(`/cards/${card.id}/notes/${n.id}/react`, { type });
      setNotes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const logTouch = async () => {
    if (!touchType) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/touch`, { type: touchType, outcome: touchOutcome || null, note: touchNote.trim() });
      setCard(data);
      onChanged?.(data);
      api.get(`/cards/${card.id}/activity`).then(({ data }) => setActivity(data)).catch(() => {});
      setTouchType(""); setTouchOutcome(""); setTouchNote("");
      toast.success("Touch logged");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const refresh = (updated) => {
    setCard(updated);
    onChanged?.(updated);
  };

  const doAction = async (label, fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      refresh(data);
      toast.success(label);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = (idx) => doAction(
    card.tasks_state[idx].done ? "Task unchecked" : "Task completed",
    () => api.post(`/cards/${card.id}/tasks/${idx}/toggle`)
  );

  const advance = () => doAction("Advanced", () => api.post(`/cards/${card.id}/advance`));
  const skip = () => doAction("Stage skipped", () => api.post(`/cards/${card.id}/skip`));
  const back = () => doAction("Moved back", () => api.post(`/cards/${card.id}/move-back`));
  const unblock = () => doAction("Unblocked", () => api.post(`/cards/${card.id}/unblock`));
  const block = () => {
    if (!blockReason.trim()) return toast.error("Reason required");
    doAction("Blocked", () => api.post(`/cards/${card.id}/block`, { reason: blockReason }));
    setBlockReason("");
  };
  const archive = () => {
    if (!archiveReason) return;
    doAction("Archived as Lost", () => api.post(`/cards/${card.id}/archive`, { reason: archiveReason, note: archiveNote }));
    setShowArchive(false);
  };
  const restore = () => doAction("Restored", () => api.post(`/cards/${card.id}/restore`));

  const sendToAvidaCare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/send-to-avida-care`);
      refresh(data);
      if (data.avida_care_sync_status === "sent") toast.success("Sent to Avida Care");
      else toast.error(`Avida Care: ${data.avida_care_last_error || "send failed"}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const saveOwner = (owner_id) => doAction("Owner updated", () => api.patch(`/cards/${card.id}`, { owner_id }));
  const formatPhone = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const saveClientDetails = () => {
    const nm = fullName.trim();
    if (nm.length < 2) return toast.error("Name must be at least 2 characters");
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length !== 10) return toast.error("Phone must be a 10-digit US number (or blank)");
    const patch = isRecruit
      ? { full_name: nm, phone, dob: dob || null, position, email, certifications: certs }
      : { full_name: nm, payer_mco: mco, phone, dob: dob || null };
    doAction(isRecruit ? "Candidate details saved" : "Client details saved", () => api.patch(`/cards/${card.id}`, patch));
  };
  const saveNextAction = () => doAction("Next action saved", () => api.patch(`/cards/${card.id}`, { next_action_date: nextDate, next_action_text: nextText }));

  // Onboarding & compliance (post-Services-Start)
  const ob = card.onboarding || {};
  const recertWeeks = ob.recert_interval_weeks ?? (String(card.payer_mco || "").toLowerCase().includes("icare") ? 26 : 52);
  const selectedPcw = pcws.find((p) => p.id === ob.pcw_id);
  const clientActDone = ONBOARDING_CLIENT_ITEMS.filter(([k]) => ob[k]).length;
  const saveOb = (patch) => doAction("Onboarding saved", () => api.patch(`/cards/${card.id}/onboarding`, patch));
  const addDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const dueBadge = (dueStr, completed) => {
    if (completed) return <span className="text-[11px] font-bold text-[#166534]">Completed</span>;
    if (!dueStr) return null;
    const left = Math.ceil((new Date(dueStr) - new Date()) / 86400000);
    const cls = left < 0 ? "text-[#b91c1c]" : left <= 14 ? "text-[#E65100]" : "text-[#15233B]/55";
    const txt = left < 0 ? `Overdue by ${-left}d` : left === 0 ? "Due today" : `Due in ${left}d`;
    return <span className={`text-[11px] font-bold ${cls}`}>{txt}</span>;
  };

  const addTask = () => {
    const title = newTaskTitle.trim();
    if (!title) return toast.error("Title required");
    doAction("Task added", () =>
      api.post(`/cards/${card.id}/tasks`, { title, due_date: newTaskDue || null })
    );
    setNewTaskTitle("");
    setNewTaskDue("");
    setAddingTask(false);
  };
  const removeTask = (idx) => {
    if (!confirm("Remove this ad-hoc task?")) return;
    doAction("Task removed", () => api.delete(`/cards/${card.id}/tasks/${idx}`));
  };
  const updateTaskDue = (idx, due_date) =>
    doAction("Task due updated", () =>
      api.patch(`/cards/${card.id}/tasks/${idx}`, { due_date: due_date || null })
    );
  const updateTaskAssignee = (idx, assigned_to) =>
    doAction("Task assignee updated", () =>
      api.patch(`/cards/${card.id}/tasks/${idx}`, { assigned_to: assigned_to || null })
    );

  const done = card.tasks_state.filter((t) => t.done).length;
  const snoozeHistory = activity.filter((a) => a.action === "snoozed" || a.action === "unsnoozed");
  const total = card.tasks_state.length;
  const allDone = total > 0 && done === total;
  const overdue = isOverdueInStage(card, template);
  const daysOver = daysOverdue(card, template);
  const days = Math.floor(daysBetween(card.stage_entered_at));
  const isArchived = card.status === "archived";
  const originMissing = (!isRecruit && card.current_stage === 1)
    ? [
        !card.sourced_by_id && "Sourced by",
        !card.referral_partner_id && "Referral partner (source)",
        !card.owner_id && "Intake owner",
      ].filter(Boolean)
    : [];
  const blockedFromAdvance = originMissing.length > 0;
  const finalStage = isRecruit ? 109 : 16;
  const sc = stageColor(card.current_stage);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="card-detail-panel">
      <button className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} aria-label="close-overlay" />
      <div className="w-full max-w-md bg-white shadow-2xl border-l border-[#15233B]/10 flex flex-col overflow-hidden">
        <div className="px-6 py-4 bg-white border-b border-[#15233B]/10 flex items-center gap-3">
          <button
            type="button"
            onClick={() => { onClose?.(); navigate("/"); }}
            data-testid="panel-stage-jump"
            title={`Go to Stage ${card.current_stage} column on the board`}
            className="h-12 w-12 shrink-0 rounded-lg grid place-items-center text-base font-mono font-extrabold shadow-sm ring-1 ring-black/5 hover:ring-2 hover:ring-[#FB923C] transition-all"
            style={{ backgroundColor: sc.primary, color: sc.text }}
          >
            {String(card.current_stage > 100 ? card.current_stage - 100 : card.current_stage).padStart(2, "0")}
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/55">
              {isRecruit ? `Candidate · Stage ${card.current_stage - 100} of 9` : `Stage ${card.current_stage} of 16`}
            </div>
            <div className="text-lg font-extrabold tracking-tight text-[#15233B] truncate">
              {template?.name}
            </div>
            <div className="text-xs text-[#15233B]/60 mt-0.5 truncate">
              {card.full_name} · <span className="font-mono">{card.av_id}</span> · {isRecruit ? (card.position || "Caregiver applicant") : card.payer_mco}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-panel-button" className="p-2 rounded hover:bg-[#15233B]/5 text-[#15233B]/70 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Aging banner */}
          <div data-testid="aging-banner" className="rounded-md p-3 border text-sm bg-[#39FF14] border-[#15233B]/20 text-[#15233B] flex items-center gap-2">
            <span><span className="font-bold">{days}d in stage</span> · target {template?.day_target}d {overdue ? <span className="font-extrabold text-[#b91c1c]">· overdue</span> : ""}</span>
            {card.created_at && (
              <span className="ml-auto text-[12px] font-semibold text-[#15233B]/70" data-testid="detail-captured">
                Captured {new Date(card.created_at).toLocaleDateString()}
              </span>
            )}
            <OverdueSiren daysOver={daysOver} className={`text-base ${card.created_at ? "" : "ml-auto"}`} />
          </div>

          {isRecruit && (
            <ResumeBox
              card={card}
              reloadKey={docReload}
              archiveReasons={archiveReasons}
              onMarked={(data) => {
                refresh(data);
                api.get(`/cards/${card.id}/activity`).then(({ data: a }) => setActivity(a)).catch(() => {});
              }}
              onNoteSaved={() => api.get(`/cards/${card.id}/notes`).then(({ data }) => setNotes(data || [])).catch(() => {})}
              onLeave={({ touched, last }) => {
                touched.forEach((c) => onChanged?.(c));
                onChanged?.(last);
              }}
            />
          )}

          {/* Snooze */}
          {!isArchived && (
            <div data-testid="snooze-control" className={`rounded-md border p-3 ${isSnoozed ? "border-[#4F46E5]/30 bg-[#EEF2FF]" : "border-[#15233B]/10 bg-white"}`}>
              {isSnoozed ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#4F46E5] flex-wrap">
                    <Moon className="h-4 w-4" />
                    <span data-testid="snooze-status">Snoozed until {card.snoozed_until}</span>
                    {card.snooze_reason && <span className="font-normal text-[#15233B]/60">· {card.snooze_reason}</span>}
                    {card.snoozed_by_name && <span className="font-normal text-[#15233B]/60">· by {card.snoozed_by_name}</span>}
                  </div>
                  <button
                    data-testid="wake-now-button"
                    onClick={wake}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-md border border-[#4F46E5]/40 text-[#4F46E5] text-xs font-extrabold hover:bg-[#4F46E5]/10 disabled:opacity-60"
                  >
                    Wake now
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 inline-flex items-center gap-1 shrink-0">
                      <Moon className="h-3.5 w-3.5" /> Snooze
                    </span>
                    <input
                      data-testid="snooze-reason-input"
                      value={snoozeReason}
                      onChange={(e) => setSnoozeReason(e.target.value)}
                      placeholder="Reason (required) — e.g. waiting on callback"
                      maxLength={200}
                      className="flex-1 min-w-[160px] text-xs px-2 py-1.5 rounded-md border border-[#15233B]/15 focus:border-[#4F46E5] focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[30, 60, 90].map((d) => (
                      <button
                        key={d}
                        data-testid={`snooze-${d}-button`}
                        onClick={() => snooze(d)}
                        disabled={busy || !snoozeReason.trim()}
                        title={!snoozeReason.trim() ? "Add a reason first" : `Snooze ${d} days`}
                        className="px-2.5 py-1 rounded-md border border-[#15233B]/15 text-xs font-bold text-[#15233B] hover:border-[#4F46E5] hover:bg-[#4F46E5]/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#15233B]/15 disabled:hover:bg-transparent"
                      >
                        {d} days
                      </button>
                    ))}
                    <span className="text-[11px] text-[#15233B]/45">
                      {snoozeReason.trim() ? "pauses it from overdue, stuck & daily lists" : "add a reason to enable"}
                    </span>
                  </div>
                </div>
              )}
              {snoozeHistory.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#15233B]/10" data-testid="snooze-history">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#15233B]/45 mb-1.5">Snooze history</div>
                  <ul className="space-y-1">
                    {snoozeHistory.slice(0, 6).map((a) => (
                      <li key={a.id} className="text-[11px] text-[#15233B]/65 leading-snug">
                        <span className="font-semibold text-[#15233B]/80">{a.action === "snoozed" ? "Snoozed" : "Woken"}</span>
                        {a.details ? ` · ${a.details}` : ""}
                        <span className="text-[#15233B]/40"> — {new Date(a.timestamp).toLocaleDateString()} · {a.user_name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Client details (editable) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">{isRecruit ? "Candidate details" : "Client details"}</div>
              <span className="text-[10px] font-mono text-[#15233B]/45">{card.av_id}</span>
            </div>
            <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Full name</label>
            <input
              data-testid="edit-full-name"
              value={fullName}
              disabled={isArchived}
              maxLength={100}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full mt-1 mb-2 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
            />
            {!isRecruit && (
              <>
                <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Payer / MCO</label>
                <select
                  data-testid="edit-mco"
                  value={mco}
                  disabled={isArchived}
                  onChange={(e) => setMco(e.target.value)}
                  className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                >
                  {[...new Set([card.payer_mco, ...(lists.mco_list || [])].filter(Boolean))].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </>
            )}
            {isRecruit && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Role type</label>
                  <select
                    data-testid="edit-position"
                    value={position}
                    disabled={isArchived}
                    onChange={(e) => setPosition(e.target.value)}
                    className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                  >
                    <option value="">— select role —</option>
                    {(position && !ROLE_TYPES.includes(position) ? [...ROLE_TYPES, position] : ROLE_TYPES).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Email</label>
                  <input
                    data-testid="edit-email"
                    type="email"
                    value={email}
                    disabled={isArchived}
                    placeholder="name@email.com"
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Certifications / licenses</label>
                  <input
                    data-testid="edit-certifications"
                    value={certs}
                    disabled={isArchived}
                    placeholder="CNA, CPR, First Aid…"
                    onChange={(e) => setCerts(e.target.value)}
                    className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Phone</label>
                <input
                  data-testid="edit-phone"
                  type="tel"
                  value={phone}
                  disabled={isArchived}
                  placeholder="(555) 123-4567"
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45">Date of birth</label>
                <input
                  data-testid="edit-dob"
                  type="date"
                  value={dob}
                  disabled={isArchived}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDob(e.target.value)}
                  className="w-full mt-1 border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-[#15233B]/5"
                />
              </div>
            </div>
            {!isArchived && (fullName.trim() !== (card.full_name || "") || mco !== (card.payer_mco || "") || phone !== (card.phone || "") || dob !== (card.dob || "") || (isRecruit && (position !== (card.position || "") || email !== (card.email || "") || certs !== (card.certifications || "")))) && (
              <div className="mt-2 flex justify-end">
                <button
                  data-testid="save-client-details"
                  onClick={saveClientDetails}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-md bg-[#FFCB05] text-[#15233B] text-xs font-extrabold hover:bg-[#FBC02D] disabled:opacity-60"
                >
                  {isRecruit ? "Save candidate details" : "Save client details"}
                </button>
              </div>
            )}
            {isRecruit && !isArchived && (
              <ResumeReparse
                card={card}
                onChanged={(c) => { setCard(c); onChanged?.(c); }}
                onActivity={() => setDocReload((x) => x + 1)}
              />
            )}
          </div>

          {/* Requested client assignment (recruiting only) */}
          {isRecruit && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Requested client assignment</div>
              <select
                data-testid="requested-client-select"
                value={card.requested_client_id || ""}
                disabled={isArchived}
                onChange={(e) => doAction("Requested client saved", () => api.patch(`/cards/${card.id}`, { requested_client_id: e.target.value || null }))}
                className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— none —</option>
                {[...clientOptions, ...(card.requested_client_id && !clientOptions.some((c) => c.id === card.requested_client_id) ? [{ id: card.requested_client_id, name: card.requested_client_name || "(unknown)" }] : [])].map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <div className="mt-1 text-[10px] text-[#15233B]/55">If a client or family requested this caregiver specifically.</div>
            </div>
          )}

          {/* Documents (candidates) */}
          {isRecruit && (
            <DocumentsSection card={card} isArchived={isArchived} reloadKey={docReload} onActivity={() => { setDocReload((x) => x + 1); api.get(`/cards/${card.id}`).then(({ data }) => onChanged?.(data)).catch(() => {}); }} />
          )}

          {/* Onboarding & compliance (post-Services-Start) */}
          {card.current_stage === 16 && (
            <div data-testid="onboarding-section" className="onboarding-panel rounded-lg border-2 border-[#FFCB05] p-4 space-y-4">
              <div aria-hidden className="absolute pointer-events-none" style={{ right: -24, bottom: -24, width: 190, height: 190, backgroundImage: `url(${process.env.PUBLIC_URL}/brand/avida-icon.png)`, backgroundSize: "contain", backgroundRepeat: "no-repeat", opacity: 0.07 }} />
              <div className="relative z-10 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#15233B]">Onboarding &amp; Compliance</div>
                <span className="text-[10px] font-bold text-[#15233B]/60 bg-white/70 px-2 py-0.5 rounded">Client {clientActDone}/{ONBOARDING_CLIENT_ITEMS.length}</span>
              </div>

              <div className="relative z-10">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 mb-1.5">Client activation</div>
                <div className="space-y-1.5">
                  {ONBOARDING_CLIENT_ITEMS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid={`ob-${key}`} checked={!!ob[key]} disabled={isArchived || busy}
                        onChange={(e) => saveOb({ [key]: e.target.checked })}
                        className="h-4 w-4 rounded border-[#15233B]/30 text-[#166534] focus:ring-[#166534]" />
                      <span className={ob[key] ? "line-through text-[#15233B]/45" : "text-[#15233B]"}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="relative z-10">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 mb-1">Assigned PCW</div>
                <select data-testid="ob-pcw-select" value={ob.pcw_id || ""} disabled={isArchived}
                  onChange={(e) => saveOb({ pcw_id: e.target.value || null })}
                  className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white">
                  <option value="">— none —</option>
                  {pcws.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.onboarding_done}/{p.onboarding_total})</option>)}
                </select>
                {selectedPcw && (
                  <div className="mt-1 text-[11px] text-[#15233B]/70">
                    PCW onboarding: <b className={selectedPcw.onboarding_complete ? "text-[#166534]" : "text-[#E65100]"}>{selectedPcw.onboarding_done}/{selectedPcw.onboarding_total} complete</b>
                    {" · "}<button type="button" onClick={() => { onClose?.(); navigate("/pcws"); }} className="underline font-semibold">manage</button>
                  </div>
                )}
                {pcws.length === 0 && <div className="mt-1 text-[10px] text-[#15233B]/55">No PCWs yet — add them under the PCWs tab.</div>}
              </div>

              <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white/70 rounded-md p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 mb-1">50–60 day supervisory</div>
                  <input type="date" data-testid="ob-supervisory-due" value={ob.supervisory_due_on || ""} disabled={isArchived}
                    onChange={(e) => saveOb({ supervisory_due_on: e.target.value || null })}
                    className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white" />
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <label className="text-[10px] flex items-center gap-1 text-[#15233B]/70">
                      <input type="checkbox" data-testid="ob-supervisory-done" checked={!!ob.supervisory_completed_on} disabled={isArchived}
                        onChange={(e) => saveOb({ supervisory_completed_on: e.target.checked ? new Date().toISOString().slice(0, 10) : null })} /> completed
                    </label>
                    {dueBadge(ob.supervisory_due_on, ob.supervisory_completed_on)}
                  </div>
                  {!ob.supervisory_due_on && !isArchived && (
                    <button type="button" onClick={() => saveOb({ supervisory_due_on: addDaysISO(55) })} className="mt-1 text-[10px] text-[#166534] underline">Set +55 days</button>
                  )}
                </div>
                <div className="bg-white/70 rounded-md p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 mb-1">Recertification</div>
                  <input type="date" data-testid="ob-recert-due" value={ob.recert_due_on || ""} disabled={isArchived}
                    onChange={(e) => saveOb({ recert_due_on: e.target.value || null })}
                    className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white" />
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-[#15233B]/70">
                    <span>every</span>
                    <input type="number" data-testid="ob-recert-weeks" value={recertWeeks} min={1} max={260} disabled={isArchived}
                      onChange={(e) => saveOb({ recert_interval_weeks: parseInt(e.target.value) || 52 })}
                      className="w-14 border border-[#15233B]/15 rounded px-1 py-0.5 text-xs bg-white" />
                    <span>wks</span>
                    <span className="ml-auto">{dueBadge(ob.recert_due_on, null)}</span>
                  </div>
                  {!ob.recert_due_on && !isArchived && (
                    <button type="button" onClick={() => saveOb({ recert_due_on: addDaysISO(recertWeeks * 7) })} className="mt-1 text-[10px] text-[#166534] underline">Set +{recertWeeks} wks</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Blocked banner */}
          {card.blocked && (
            <div className="rounded-md p-3 bg-[#b91c1c]/10 border-2 border-[#b91c1c]/50 text-[#b91c1c] flex items-start gap-3">
              <Ban className="h-5 w-5 mt-0.5" />
              <div className="text-sm">
                <div className="font-extrabold uppercase tracking-[0.18em] text-[12px]">Blocked</div>
                <div className="mt-1 font-bold">{card.blocked_reason}</div>
              </div>
              {!isArchived && (
                <button data-testid="unblock-button" onClick={unblock} className="ml-auto text-xs font-bold underline">
                  Unblock
                </button>
              )}
            </div>
          )}

          {/* Tasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
                Tasks ({done}/{total})
              </div>
              {!isArchived && (
                <button
                  onClick={() => setAddingTask(true)}
                  data-testid="add-task-button"
                  className="text-xs font-semibold text-[#FBBF24] hover:underline"
                >
                  + Add task
                </button>
              )}
            </div>
            <ul className="space-y-2">
              {card.tasks_state.map((t, idx) => {
                const taskOverdue = t.due_date && !t.done && t.due_date <= new Date().toISOString().slice(0, 10);
                return (
                <li key={idx} className="group">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={t.done}
                      disabled={isArchived || busy}
                      onChange={() => toggleTask(idx)}
                      data-testid={`task-checkbox-${idx}`}
                      className="mt-0.5 h-4 w-4 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm ${t.done ? "line-through text-[#15233B]/40" : "text-[#15233B]"}`}>
                          {t.title}
                        </span>
                        {t.ad_hoc && (
                          <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#FBBF24] bg-[#FBBF24]/10 px-1.5 py-0.5 rounded">
                            ad-hoc
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <input
                          type="date"
                          value={t.due_date || ""}
                          disabled={isArchived}
                          onChange={(e) => updateTaskDue(idx, e.target.value)}
                          data-testid={`task-due-${idx}`}
                          className={`text-xs border rounded px-1.5 py-0.5 bg-white ${taskOverdue ? "border-[#b91c1c] text-[#b91c1c]" : "border-[#15233B]/15 text-[#15233B]/70"}`}
                        />
                        {taskOverdue && (
                          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#b91c1c]">overdue</span>
                        )}
                        <div className="flex items-center gap-1">
                          {(() => {
                            const assigned = users.find((u) => u.id === t.assigned_to);
                            const oc = ownerColor(t.assigned_to);
                            return assigned ? (
                              <span
                                className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold"
                                style={{ backgroundColor: oc.primary, color: oc.text }}
                                title={`Assigned to ${assigned.name}`}
                              >
                                {assigned.name[0]}
                              </span>
                            ) : (
                              <span className="text-[10px] text-[#15233B]/40 italic">Unassigned</span>
                            );
                          })()}
                          <select
                            value={t.assigned_to || ""}
                            disabled={isArchived}
                            onChange={(e) => updateTaskAssignee(idx, e.target.value)}
                            data-testid={`task-assignee-${idx}`}
                            className="text-xs border border-[#15233B]/15 rounded px-1.5 py-0.5 bg-white max-w-[110px]"
                          >
                            <option value="">— assign —</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    {t.ad_hoc && !isArchived && (
                      <button
                        onClick={() => removeTask(idx)}
                        data-testid={`task-remove-${idx}`}
                        className="text-xs text-[#b91c1c] opacity-0 group-hover:opacity-100 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              );})}
            </ul>
            {addingTask && (
              <div className="mt-3 flex gap-2 items-center" data-testid="add-task-row">
                <input
                  autoFocus
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="New task title"
                  data-testid="new-task-title"
                  className="flex-1 border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white"
                />
                <input
                  type="date"
                  value={newTaskDue}
                  onChange={(e) => setNewTaskDue(e.target.value)}
                  data-testid="new-task-due"
                  className="border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white"
                />
                <button onClick={addTask} data-testid="new-task-add" className="text-xs font-bold px-2 py-1 rounded bg-[#FBBF24] text-white">Add</button>
                <button onClick={() => { setAddingTask(false); setNewTaskTitle(""); setNewTaskDue(""); }} className="text-xs text-[#15233B]/60">Cancel</button>
              </div>
            )}
          </div>

          {/* Next action */}
          <div className="bg-[#F6F3EC] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Next action</div>
            <div className="flex gap-2">
              <input
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                data-testid="next-action-date-input"
                className="border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
              <input
                value={nextText}
                onChange={(e) => setNextText(e.target.value)}
                placeholder="What's next?"
                data-testid="next-action-text-input"
                className="flex-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <button data-testid="save-next-action-button" onClick={saveNextAction} className="mt-2 text-xs font-semibold text-[#FBBF24] hover:underline">
              Save next action
            </button>
          </div>

          {/* Send to Avida Care (intake clients, once past Screening) */}
          {!isRecruit && !isArchived && card.current_stage >= 3 && (
            <div data-testid="avida-care-section" className="rounded-lg border border-[#1E73C9]/25 bg-[#1E73C9]/[0.04] p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E73C9]">Avida Care platform</div>
                  <div className="text-xs mt-0.5">
                    {card.avida_care_sync_status === "sent" ? (
                      <span className="text-[#166534] font-semibold" data-testid="avida-care-status">
                        Sent{card.avida_care_synced_at ? ` ${new Date(card.avida_care_synced_at).toLocaleDateString()}` : ""}
                        {card.avida_care_external_id ? ` · ID ${card.avida_care_external_id}` : ""}
                      </span>
                    ) : card.avida_care_sync_status === "failed" ? (
                      <span className="text-[#b91c1c] font-semibold" data-testid="avida-care-status">Last send failed — {card.avida_care_last_error || "error"}</span>
                    ) : (
                      <span className="text-[#15233B]/60" data-testid="avida-care-status">Not yet sent to Avida Care.</span>
                    )}
                  </div>
                </div>
                <button
                  data-testid="send-to-avida-care-button"
                  onClick={sendToAvidaCare}
                  disabled={busy}
                  className="px-3 py-2 rounded-md bg-[#1E73C9] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#1565C0] disabled:opacity-50 shrink-0"
                >
                  <Forward className="h-3.5 w-3.5" /> {card.avida_care_sync_status === "sent" ? "Re-send" : "Send to Avida Care"}
                </button>
              </div>
            </div>
          )}

          {/* Compliance: PA expiration & DHS-12 (admin + everyone can edit) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!isRecruit && (
            <div className="bg-white rounded-md p-3 border border-[#15233B]/10">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">PA expires on</div>
              <input
                type="date"
                value={card.pa_expires_on || ""}
                disabled={isArchived}
                onChange={(e) => doAction("PA expiration saved", () => api.patch(`/cards/${card.id}`, { pa_expires_on: e.target.value || null }))}
                data-testid="pa-expires-input"
                className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
              {card.pa_expires_on && (() => {
                const d = (new Date(card.pa_expires_on) - new Date()) / 86400000;
                const left = Math.ceil(d);
                const cls = left < 0 ? "text-[#b91c1c]" : left <= 30 ? "text-[#E65100]" : "text-[#15233B]/55";
                return (
                  <div className={`mt-1.5 text-[11px] font-bold ${cls}`}>
                    {left < 0 ? `Expired ${-left}d ago` : left === 0 ? "Expires today" : `Expires in ${left}d`}
                  </div>
                );
              })()}
            </div>
            )}
            <div className="bg-white rounded-md p-3 border border-[#15233B]/10">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">DHS-12 background check</div>
              <select
                value={card.dhs12_status || ""}
                disabled={isArchived}
                onChange={(e) => doAction("DHS-12 status saved", () => api.patch(`/cards/${card.id}`, { dhs12_status: e.target.value }))}
                data-testid="dhs12-status-select"
                className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">Not yet started</option>
                <option value="pending">Pending</option>
                <option value="cleared">Cleared</option>
                <option value="expired">Expired</option>
              </select>
              <input
                type="date"
                value={card.dhs12_expires_on || ""}
                disabled={isArchived || card.dhs12_status !== "cleared"}
                onChange={(e) => doAction("DHS-12 expiration saved", () => api.patch(`/cards/${card.id}`, { dhs12_expires_on: e.target.value || null }))}
                data-testid="dhs12-expires-input"
                className="mt-1.5 w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white"
                placeholder="Clearance expires on"
              />
            </div>
          </div>

          {/* Owner */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Owner</div>
            <select
              data-testid="owner-select"
              value={users.some((u) => u.id === card.owner_id) ? card.owner_id : ""}
              disabled={isArchived}
              onChange={(e) => saveOwner(e.target.value)}
              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-white ${users.some((u) => u.id === card.owner_id) ? "border-[#15233B]/15" : "border-[#b91c1c]/40 text-[#b91c1c]"}`}
            >
              <option value="" disabled>— Unassigned — choose an owner</option>
              {users.filter((u) => u.role !== "field_agent").map((u) => (
                <option key={u.id} value={u.id}>{`${u.name} (${u.role})`}</option>
              ))}
            </select>
          </div>

          {/* Sourced by (lead originator) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Sourced by</div>
            <select
              data-testid="sourced-by-select"
              value={card.sourced_by_id || ""}
              disabled={isArchived}
              onChange={(e) => doAction("Sourced by updated", () => api.patch(`/cards/${card.id}`, { sourced_by_id: e.target.value || null }))}
              className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">— none —</option>
              {users.filter((u) => u.role !== "field_agent").map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#15233B]/55">Who brought this lead in. Stays fixed as the lead advances.</div>
          </div>

          {/* Field agent (marketing) */}
          {!isRecruit && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">
              Field agent (marketing)
            </div>
            <select
              data-testid="field-agent-select"
              value={card.field_agent_id || ""}
              disabled={isArchived}
              onChange={(e) => doAction("Field agent updated", () => api.patch(`/cards/${card.id}`, { field_agent_id: e.target.value || null }))}
              className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">— none —</option>
              {[...agents, ...(card.field_agent_id && !agents.some((a) => a.id === card.field_agent_id) ? [{ id: card.field_agent_id, name: card.field_agent_name || "(unknown)" }] : [])].map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#15233B]/55">
              The marketing/field agent for this lead. Manage under Partners → Field Agents.
            </div>
          </div>
          )}

          {/* Caregiver (Personal Care Worker) */}
          {!isRecruit && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">
              Caregiver (PCW)
            </div>
            <select
              data-testid="caregiver-select"
              value={card.caregiver || ""}
              disabled={isArchived}
              onChange={(e) => doAction("Caregiver updated", () => api.patch(`/cards/${card.id}`, { caregiver: e.target.value || "" }))}
              className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">— not assigned —</option>
              {(lists.caregivers || []).map((cg) => (
                <option key={cg} value={cg}>{cg}</option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#15233B]/55">
              Shown on Services Start cards. Manage the list in Settings → Dropdown lists.
            </div>
          </div>
          )}

          {/* Referral partner (lead source) */}
          {!isRecruit && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">
              Referral partner (source)
            </div>
            <select
              data-testid="referral-partner-select"
              value={card.referral_partner_id || ""}
              disabled={isArchived}
              onChange={(e) => doAction("Referral partner updated", () => api.patch(`/cards/${card.id}`, { referral_partner_id: e.target.value || null }))}
              className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">— none —</option>
              {[...partners, ...(card.referral_partner_id && !partners.some((p) => p.id === card.referral_partner_id) ? [{ id: card.referral_partner_id, name: card.referral_partner_name || "(unknown)", category: "" }] : [])].map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.category ? ` · ${p.category}` : ""}</option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#15233B]/55">Where this lead came from. Manage under the Partners tab.</div>
          </div>
          )}

          {/* Event / Campaign (independent optional) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">
              Event / Campaign
            </div>
            <select
              data-testid="marketing-effort-select"
              value={card.marketing_effort_id || ""}
              disabled={isArchived}
              onChange={(e) => doAction("Event/Campaign updated", () => api.patch(`/cards/${card.id}`, { marketing_effort_id: e.target.value || null }))}
              className="w-full border border-[#15233B]/15 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">— none —</option>
              {[...efforts, ...(card.marketing_effort_id && !efforts.some((x) => x.id === card.marketing_effort_id) ? [{ id: card.marketing_effort_id, name: card.marketing_effort_name || "(unknown)", type: "" }] : [])].map((x) => (
                <option key={x.id} value={x.id}>{x.name}{x.type ? ` (${x.type})` : ""}</option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#15233B]/55">If this lead came from a marketing event/campaign. Manage in Settings → Events &amp; Campaigns.</div>
          </div>

          {/* Block */}
          {!card.blocked && !isArchived && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Block card</div>
              <div className="flex gap-2">
                <input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Reason (e.g., MD office unresponsive)"
                  data-testid="block-reason-input"
                  className="flex-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                />
                <button
                  onClick={block}
                  data-testid="block-button"
                  className="px-3 py-1.5 bg-[#7e22ce] text-white text-xs font-bold rounded hover:bg-[#6b1ba8]"
                >
                  Block
                </button>
              </div>
            </div>
          )}

          {/* Log a touch */}
          {!isArchived && (
            <div data-testid="log-touch" className="rounded-lg border border-[#15233B]/10 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Log a touch</div>
                <button
                  type="button"
                  data-testid="send-email-button"
                  onClick={() => setShowEmail(true)}
                  className="inline-flex items-center gap-1 text-xs font-extrabold px-2.5 py-1.5 rounded-md bg-[#FFCB05] text-[#15233B] hover:bg-[#FBC02D] transition-colors"
                >
                  <Mail className="h-3.5 w-3.5" /> Send email
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {TOUCH_TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`touch-type-${t.key}`}
                    onClick={() => setTouchType((p) => (p === t.key ? "" : t.key))}
                    className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-md border transition-colors ${touchType === t.key ? "border-[#1E73C9] bg-[#1E73C9]/10 text-[#1E73C9]" : "border-[#15233B]/15 text-[#15233B]/70 hover:bg-[#15233B]/5"}`}
                  >
                    <t.Icon className="h-3.5 w-3.5" /> {t.label}
                  </button>
                ))}
              </div>
              {touchType && (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {TOUCH_OUTCOMES.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`touch-outcome-${o.key}`}
                        onClick={() => setTouchOutcome((p) => (p === o.key ? "" : o.key))}
                        className={`text-[11px] font-semibold px-2 py-1 rounded border transition-colors ${touchOutcome === o.key ? "border-[#166534] bg-[#166534]/10 text-[#166534]" : "border-[#15233B]/15 text-[#15233B]/60 hover:bg-[#15233B]/5"}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <input
                    data-testid="touch-note"
                    value={touchNote}
                    onChange={(e) => setTouchNote(e.target.value)}
                    placeholder="Optional note…"
                    className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => { setTouchType(""); setTouchOutcome(""); setTouchNote(""); }}
                      className="text-xs font-semibold text-[#15233B]/50 hover:text-[#15233B] px-2 py-1.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="touch-save-button"
                      onClick={logTouch}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-md bg-[#15233B] text-white text-xs font-extrabold hover:bg-[#1E2F4D] disabled:opacity-50"
                    >
                      Log {TOUCH_TYPES.find((t) => t.key === touchType)?.label}
                    </button>
                  </div>
                </div>
              )}
              {(card.touch_count || card.last_touch_type) && (
                <div className="mt-2 text-[11px] text-[#15233B]/55" data-testid="touch-summary">
                  {card.touch_count ? `${card.touch_count} touch${card.touch_count === 1 ? "" : "es"}` : ""}
                  {card.contact_attempts > 1 ? ` · ${card.contact_attempts} attempts since reached` : ""}
                  {card.last_touch_type ? ` · last: ${TOUCH_LABEL[card.last_touch_type] || card.last_touch_type}${card.last_touch_outcome ? ` (${card.last_touch_outcome.replace(/_/g, " ")})` : ""}` : ""}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div data-testid="notes-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
                Notes <span className="ml-1 normal-case tracking-normal font-normal text-[#15233B]/45">(@firstname to tag)</span>
              </div>
              {card.last_contacted_at && (
                <span className="text-[10px] font-semibold text-[#15233B]/55" data-testid="detail-last-contacted">
                  Last contact {new Date(card.last_contacted_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {!isArchived && (
              <div className="mb-3">
                <textarea
                  data-testid="new-note-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Add a note for the team…"
                  className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                />
                <div className="mt-1 flex justify-end">
                  <button
                    data-testid="add-note-button"
                    onClick={addNote}
                    disabled={busy || !newNote.trim()}
                    className="px-3 py-1.5 rounded-md bg-[#FFCB05] text-[#15233B] text-xs font-extrabold hover:bg-[#FBC02D] disabled:opacity-50"
                  >
                    Add note
                  </button>
                </div>
              </div>
            )}
            <ul className="space-y-2" data-testid="notes-list">
              {sortNotes(notes).map((n) => (
                <li
                  key={n.id}
                  data-testid={`note-${n.id}`}
                  className={`rounded-md border p-2.5 text-sm ${n.pinned ? "border-[#FBBF24] bg-[#FFF9C4]/40" : "border-[#15233B]/10 bg-white"}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold"
                      style={{ backgroundColor: ownerColor(n.author_id).primary, color: ownerColor(n.author_id).text }}
                    >
                      {(n.author_name || "?")[0]}
                    </span>
                    <span className="text-[11px] font-bold text-[#15233B]">{n.author_name}</span>
                    <span className="text-[10px] text-[#15233B]/45">{new Date(n.created_at).toLocaleString()}</span>
                    {n.pinned && (
                      <span className="text-[9px] uppercase tracking-[0.15em] font-extrabold text-[#8a6d00] bg-[#FFCB05]/30 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                        <Pin className="h-2.5 w-2.5" /> Pinned
                      </span>
                    )}
                    {!isArchived && (
                      <button
                        data-testid={`note-pin-${n.id}`}
                        onClick={() => pinNote(n)}
                        className="ml-auto text-[10px] font-semibold text-[#15233B]/50 hover:text-[#15233B] inline-flex items-center gap-0.5"
                      >
                        <Pin className="h-3 w-3" /> {n.pinned ? "Unpin" : "Pin"}
                      </button>
                    )}
                  </div>
                  {n.kind === "resume_review" && <div className="mb-1"><FitChip rating={n.rating} /></div>}
                  {n.body && <div className="text-[#15233B]/85 whitespace-pre-wrap break-words">{n.body}</div>}
                  {(() => {
                    const seen = (n.reactions || []).filter((r) => r.type === "seen");
                    const likes = (n.reactions || []).filter((r) => r.type === "thumbsup");
                    const mineSeen = seen.some((r) => r.user_id === currentUser?.id);
                    const mineLike = likes.some((r) => r.user_id === currentUser?.id);
                    return (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          data-testid={`note-react-seen-${n.id}`}
                          onClick={() => reactNote(n, "seen")}
                          title={seen.map((r) => r.user_name).join(", ") || "Mark as seen"}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors ${mineSeen ? "border-[#1E73C9]/40 text-[#1E73C9] bg-[#1E73C9]/10" : "border-[#15233B]/15 text-[#15233B]/55 hover:bg-[#15233B]/5"}`}
                        >
                          <Eye className="h-3 w-3" /> Seen{seen.length ? ` ${seen.length}` : ""}
                        </button>
                        <button
                          type="button"
                          data-testid={`note-react-thumbsup-${n.id}`}
                          onClick={() => reactNote(n, "thumbsup")}
                          title={likes.map((r) => r.user_name).join(", ") || "Thumbs up"}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors ${mineLike ? "border-[#166534]/40 text-[#166534] bg-[#166534]/10" : "border-[#15233B]/15 text-[#15233B]/55 hover:bg-[#15233B]/5"}`}
                        >
                          <ThumbsUp className="h-3 w-3" />{likes.length ? ` ${likes.length}` : ""}
                        </button>
                      </div>
                    );
                  })()}
                </li>
              ))}
              {card.note && (
                <li data-testid="legacy-note" className="rounded-md border border-dashed border-[#15233B]/20 bg-[#F6F3EC]/50 p-2.5 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/45 mb-1">Earlier note</div>
                  <div className="text-[#15233B]/80 whitespace-pre-wrap break-words">{card.note}</div>
                </li>
              )}
              {notes.length === 0 && !card.note && <li className="text-xs italic text-[#15233B]/40">No notes yet.</li>}
            </ul>
          </div>

          {/* Activity log (history) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">History</div>
            <button
              data-testid="view-history-button"
              onClick={() => setShowHistory(true)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-md border border-[#15233B]/15 bg-white hover:bg-[#F6F3EC] text-sm font-semibold text-[#15233B] transition-colors"
            >
              <span className="flex items-center gap-2">
                <History className="h-4 w-4 text-[#15233B]/55" />
                View activity log
              </span>
              <span className="text-[11px] font-bold text-[#15233B]/45">
                {activity.length} {activity.length === 1 ? "entry" : "entries"}
              </span>
            </button>
            <div className="mt-1 text-[10px] text-[#15233B]/55">A full audit trail of every action taken on this lead.</div>
          </div>

          {isArchived && (
            <div className="rounded-md p-3 bg-[#15233B]/5 border border-[#15233B]/10">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Archived</div>
              <div className="text-sm font-semibold text-[#15233B] mt-1">{card.archived_reason}</div>
              <div className="text-xs text-[#15233B]/70 mt-0.5">{card.archived_note}</div>
            </div>
          )}

          <div className="pt-2 border-t border-[#15233B]/8 flex items-center justify-between text-[10px] text-[#15233B]/40">
            <AvidaMark size="xs" withWordmark={false} />
            <span className="font-mono">{card.av_id}</span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-[#15233B]/10 bg-white flex items-center gap-2 flex-wrap">
          {!isArchived ? (
            <>
              <button
                data-testid="move-back-button"
                onClick={back}
                disabled={busy || card.current_stage <= 1}
                className="px-3 py-2 rounded-md border border-[#15233B]/15 text-[#15233B] hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1 disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
              {template?.skippable && (
                <button
                  data-testid="skip-button"
                  onClick={skip}
                  disabled={busy || blockedFromAdvance}
                  title={blockedFromAdvance ? `Set ${originMissing.join(", ")} before this lead can leave Lead Capture` : ""}
                  className="px-3 py-2 rounded-md border border-[#15233B]/15 text-[#15233B] hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1 disabled:opacity-40"
                >
                  <Forward className="h-3.5 w-3.5" /> N/A — skip
                </button>
              )}
              <button
                data-testid="archive-button"
                onClick={() => setShowArchive(true)}
                disabled={busy}
                className="px-3 py-2 rounded-md text-[#b91c1c] hover:bg-[#b91c1c]/5 text-xs font-semibold flex items-center gap-1"
              >
                <Archive className="h-3.5 w-3.5" /> Archive as Lost
              </button>
              {isManager(currentUser) && (
                <button
                  data-testid="merge-button"
                  onClick={() => setShowMerge(true)}
                  disabled={busy}
                  className="px-3 py-2 rounded-md text-[#15233B] hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1 border border-[#15233B]/15"
                >
                  <Merge className="h-3.5 w-3.5" /> Merge
                </button>
              )}
              {(card.current_stage === 6 || card.current_stage === 7) && (
                <button
                  data-testid="open-rn-form"
                  onClick={() => { onClose?.(); navigate(`/rn/${card.id}`); }}
                  className="px-3 py-2 rounded-md bg-[#1E73C9] hover:bg-[#1565C0] text-white text-xs font-bold flex items-center gap-1"
                  title="Open the field-optimized RN visit form"
                >
                  RN field visit →
                </button>
              )}
              <div className="ml-auto flex flex-col items-end gap-0.5">
                <button
                  data-testid="advance-button"
                  onClick={advance}
                  disabled={busy || !allDone || card.current_stage >= finalStage || blockedFromAdvance}
                  title={blockedFromAdvance ? `Set ${originMissing.join(", ")} before advancing` : (!allDone ? `Finish all ${total} tasks to advance.` : "")}
                  className="px-4 py-2 rounded-md bg-[#166534] text-white hover:bg-[#14532d] text-xs font-bold flex items-center gap-1 disabled:opacity-40"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Advance <ChevronRight className="h-3.5 w-3.5" />
                </button>
                {blockedFromAdvance && (
                  <span data-testid="origin-gate-hint" className="text-[10px] font-semibold text-[#b45309] text-right max-w-[240px]">
                    Set {originMissing.join(", ")} to advance.
                  </span>
                )}
                {!blockedFromAdvance && !allDone && card.current_stage < finalStage && (
                  <span data-testid="advance-tasks-hint" className="text-[10px] text-[#15233B]/60">Finish all {total} tasks to advance.</span>
                )}
              </div>
            </>
          ) : (
            <button onClick={restore} data-testid="restore-button" className="px-3 py-2 rounded-md bg-[#FBBF24] text-white text-xs font-semibold">
              Restore from archive
            </button>
          )}
        </div>

        {/* Archive modal */}
        {showArchive && (
          <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center p-6">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-extrabold tracking-tight text-[#15233B]">Archive as Lost</h3>
              <p className="text-xs text-[#15233B]/60 mt-1">Card will leave the board but stay searchable in Archived.</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Reason</label>
                  <select
                    data-testid="archive-reason-select"
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                  >
                    {archiveReasons.map((r) => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Note (optional)</label>
                  <textarea
                    rows={3}
                    data-testid="archive-note-input"
                    value={archiveNote}
                    onChange={(e) => setArchiveNote(e.target.value)}
                    className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setShowArchive(false)} className="px-3 py-2 rounded text-xs font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
                <button
                  data-testid="archive-confirm-button"
                  onClick={archive}
                  className="px-3 py-2 rounded bg-[#b91c1c] text-white text-xs font-bold"
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showMerge && (
        <MergeCardDialog
          primary={card}
          onClose={() => setShowMerge(false)}
          onMerged={(updated) => refresh(updated)}
        />
      )}
      {showHistory && (
        <div className="absolute inset-0 z-[60] flex" data-testid="history-drawer">
          <button className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setShowHistory(false)} aria-label="close-history-overlay" />
          <div className="w-full max-w-sm bg-white shadow-2xl border-l border-[#15233B]/10 flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
            <div className="px-5 py-4 border-b border-[#15233B]/10 flex items-center justify-between bg-[#15233B] text-white">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FFCB05]">Activity log</div>
                <div className="text-sm font-extrabold truncate">{card.full_name} · <span className="font-mono">{card.av_id}</span></div>
              </div>
              <button onClick={() => setShowHistory(false)} data-testid="close-history-button" className="p-2 rounded hover:bg-white/10 text-white/80 shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="flex-1 overflow-y-auto p-5 space-y-3">
              {activity.map((a) => {
                const isSnoozeEvt = a.action === "snoozed" || a.action === "unsnoozed";
                const isTouch = a.action === "touch";
                const TIcon = isTouch ? (TOUCH_ICON[a.touch_type] || Phone) : null;
                return (
                <li key={a.id} data-testid="history-entry" className={`text-xs border-l-2 pl-3 py-1 ${isTouch ? "border-[#1E73C9]" : isSnoozeEvt ? "border-[#4F46E5]" : "border-[#FBBF24]/50"}`}>
                  <div className={`font-semibold flex items-center gap-1 ${isTouch ? "text-[#1E73C9]" : isSnoozeEvt ? "text-[#4F46E5]" : "text-[#15233B]"}`}>
                    {isTouch && TIcon ? <TIcon className="h-3 w-3" /> : isSnoozeEvt ? <Moon className="h-3 w-3" /> : null}
                    {isTouch ? (TOUCH_LABEL[a.touch_type] || "Touch") : a.action.replace(/_/g, " ")}
                  </div>
                  {a.details && <div className="text-[#15233B]/70 mt-0.5">{a.details}</div>}
                  <div className="text-[10px] text-[#15233B]/40 mt-0.5">
                    {new Date(a.timestamp).toLocaleString()} · {a.user_name}
                  </div>
                </li>
              );})}
              {activity.length === 0 && <li className="text-xs italic text-[#15233B]/40">No activity yet.</li>}
            </ol>
          </div>
        </div>
      )}
      {showEmail && (
        <SendEmailDialog
          contact={card}
          workspace={isRecruit ? "recruiting" : "intake"}
          sendPath={`/cards/${card.id}/send-email`}
          onClose={() => setShowEmail(false)}
          onSent={(data) => {
            refresh(data);
            api.get(`/cards/${card.id}/activity`).then(({ data: a }) => setActivity(a)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
